import { useState } from 'react';
import { css } from '@emotion/react';
import {
  BookableEntity,
  ScheduleDefinition,
  SDBookableAppointmentType,
} from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule_definitions.pb';
import dayjs from 'dayjs';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Table,
  TableColumnConfig,
  EditIcon,
  Chip,
  Truncated,
  CopyIcon,
  TrashIcon,
  ChipVariants,
  useModalControl,
  formatDate,
  useAlert,
} from '@frontend/design-system';
import type {
  AvailabilityMode,
  OnOpenAddEditTray,
} from '../../../../../components/availability/availability-form/types';
import { getBookableEntityInfo } from '../../../../../hooks/settings/use-availability-rule-form';
import { getAvailabilityRecurrenceText } from '../../../helpers';
import { useGetAvailabilityRules } from '../../hooks/use-get-availability-rules';
import { DeleteConfirmationModal } from '../delete-confirmation-modal';
import { DuplicateConfirmationModal } from '../duplicate-confirmation-modal';

type Props = {
  availabilityMode: AvailabilityMode;
  data: ScheduleDefinition[];
  locationId: string;
  openAddEditTray: OnOpenAddEditTray;
  entityId: string;
  sourceIds?: string[];
};

export const AvailabilityRulesTable = ({
  availabilityMode,
  data,
  locationId,
  openAddEditTray,
  entityId,
  sourceIds,
}: Props) => {
  const { t } = useTranslation('schedule-settings');
  const alerts = useAlert();
  const [tempScheduleDefinition, setTempScheduleDefinition] = useState<ScheduleDefinition | null>(null);
  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();
  const { modalProps: duplicateModalProps, triggerProps: duplicateTriggerProps } = useModalControl();
  const { isV3OperatorySupported } = SchedulerV3.Helpers.useCheckV3OperatorySupport();

  const deleteScheduleDefinitionMutation = SchedulerV3.Mutations.useDeleteScheduleDefinitionByIdMutation({
    options: {
      onSuccess: () => {
        setTempScheduleDefinition(null);
        deleteModalProps.onClose();
      },
      onError: (error) => {
        console.error('Error deleting schedule definition', error);
        alerts.error(t('Failed to delete schedule definition'));
        setTempScheduleDefinition(null);
      },
    },
  });

  const { data: availabilityRulesData } = useGetAvailabilityRules({
    locationId,
    searchValue: '',
    sourceIds,
    availabilityMode,
  });

  const resourceOptions = (availabilityRulesData || []).map((rule) => ({
    id: rule.id,
    name: rule.displayName || rule.externalName,
  }));

  const colConfig: TableColumnConfig<ScheduleDefinition>[] = [
    {
      id: 'name',
      Header: t('Name'),
      accessor: 'displayName',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => cellValue || '-',
    },
    {
      id: 'status',
      Header: t('Status'),
      accessor: 'status',
      disableSortBy: true,
      width: 80,
      cellRenderer: (cellValue: string) => {
        let variant = 'critical' as ChipVariants;
        let iconName = 'remove' as IconName;

        switch (cellValue) {
          case 'ACTIVE':
            variant = 'success';
            iconName = 'check';
            break;
          case 'PENDING':
            variant = 'warn';
            iconName = 'pending';
            break;
          case 'INACTIVE':
            variant = 'neutral';
            iconName = 'block';
            break;
          case 'ERROR':
            iconName = 'remove';
            variant = 'critical';
            break;
          default:
            break;
        }

        return (
          <Chip variant={variant}>
            <Icon name={iconName} size={16} />
          </Chip>
        );
      },
    },
    {
      id: 'recurrence',
      Header: t('Recurrence'),
      accessor: 'recurrenceType',
      disableSortBy: true,
      maxWidth: 140,
      cellRenderer: (cellValue: string) => {
        let value = '';
        switch (cellValue) {
          case 'WEEKLY':
            value = t('Weekly');
            break;
          case 'SPECIFIC_DATE':
            value = t('Specific date');
            break;
          case 'CUSTOM':
            value = t('Custom');
            break;
          default:
            value = cellValue;
            break;
        }
        return value;
      },
    },
    {
      id: 'schedule',
      Header: t('Schedule'),
      accessor: 'rRule',
      disableSortBy: true,
      maxWidth: 240,
      cellRenderer: (_cellValue, rowData) =>
        rowData ? getAvailabilityRecurrenceText(rowData.rRule, rowData.startDate) : '',
    },
    {
      id: 'hours',
      Header: t('Hours'),
      accessor: 'startTime',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (_cellValue, rowData) => {
        const start = rowData?.startTime ? dayjs(rowData.startTime, 'HH:mm').format('h:mm A') : '';
        const end = rowData?.endTime ? dayjs(rowData.endTime, 'HH:mm').format('h:mm A') : '';
        return start && end ? `${start} - ${end}` : start || end || '-';
      },
    },
    {
      id: 'services',
      Header: t('Services'),
      accessor: 'bookableAppointmentTypes',
      disableSortBy: true,
      minWidth: 200,
      maxWidth: 240,
      cellRenderer: (cellValue: SDBookableAppointmentType[]) => {
        const text = cellValue?.map(({ name }) => name).join(', ');
        return <Truncated>{t(`{{text}}`, { text })}</Truncated>;
      },
    },
    {
      id: 'operatory',
      Header: t('Operatory'),
      accessor: 'bookableEntities',
      disableSortBy: true,
      maxWidth: 200,
      omit: !isV3OperatorySupported || availabilityMode === 'OPERATORY',
      cellRenderer: (cellValue) => {
        const { operatoryId } = getBookableEntityInfo(cellValue);
        const operatory = cellValue.find((entity: BookableEntity) => entity.resourceId === operatoryId);
        return operatory ? <Truncated>{operatory.resourceDisplayName}</Truncated> : '-';
      },
    },
    {
      id: 'serviceableProvider',
      Header: t('Serviceable Provider'),
      accessor: 'bookableEntities',
      disableSortBy: true,
      maxWidth: 200,
      omit: availabilityMode === 'PROVIDER',
      cellRenderer: (cellValue) => {
        const { serviceableProviderId } = getBookableEntityInfo(cellValue);
        const provider = cellValue.find((entity: BookableEntity) => entity.resourceId === serviceableProviderId);
        return provider ? <Truncated>{provider.resourceDisplayName}</Truncated> : '-';
      },
    },
    {
      id: 'primaryProvider',
      Header: t('Primary Provider'),
      accessor: 'bookableEntities',
      disableSortBy: true,
      maxWidth: 200,
      cellRenderer: (cellValue) => {
        const { billableProviderId } = getBookableEntityInfo(cellValue);
        const provider = cellValue.find((entity: BookableEntity) => entity.resourceId === billableProviderId);
        return provider ? <Truncated>{provider.resourceDisplayName}</Truncated> : '-';
      },
    },
    {
      id: 'patientBooking',
      Header: t('Patient Booking'),
      accessor: 'bookingStrategy',
      disableSortBy: true,
      maxWidth: 170,
      cellRenderer: (cellValue: string) => {
        switch (cellValue) {
          case 'DIRECT':
            return (
              <Chip leftElement={<Icon name='check-small' />} variant='success' css={{ minWidth: 'fit-content' }}>
                {t('Self Bookable')}
              </Chip>
            );
          case 'REQUEST':
            return (
              <Chip leftElement={<Icon name='help-small' />} variant='indigo' css={{ minWidth: 'fit-content' }}>
                {t('Request Only')}
              </Chip>
            );
          case 'INTERNAL':
            return (
              <Chip leftElement={<Icon name='x-small' />} variant='critical' css={{ minWidth: 'fit-content' }}>
                {t('Staff Only')}
              </Chip>
            );
          default:
            return '-';
        }
      },
    },
    {
      id: 'startDate',
      Header: t('Start Date'),
      accessor: 'startDate',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => formatDate(cellValue, 'MMM DD, YYYY'),
    },
    {
      id: 'endDate',
      Header: t('End Date'),
      accessor: 'endDate',
      disableSortBy: true,
      maxWidth: 180,
      cellRenderer: (cellValue: string) => formatDate(cellValue, 'MMM DD, YYYY'),
    },
  ];

  const handleDelete = () => {
    deleteScheduleDefinitionMutation.mutate({ id: tempScheduleDefinition?.id || '', locationId });
  };

  return (
    <>
      <Table
        data={data}
        colConfig={colConfig}
        tableStyle={tableStyle}
        emptyStateConfig={{
          type: 'missing_forms' as const,
          header: t('No Availability Configured'),
          description:
            availabilityMode === 'OPERATORY'
              ? t(
                  'Define when this operatory is open for appointments. Create weekly schedules for regular hours or add one-time blocks for special dates.'
                )
              : t(
                  'Define when this provider is available for appointments. Create weekly schedules for regular hours or add one-time blocks for special dates.'
                ),
          action: { label: t('Create Schedule'), onClick: () => openAddEditTray(null, entityId, false) },
        }}
        rowActions={{
          actions: [
            {
              label: t('Edit'),
              Icon: EditIcon,
              onClick: (rowData) => {
                openAddEditTray(rowData, entityId, false);
              },
            },
            {
              label: t('Duplicate'),
              Icon: CopyIcon,
              disabled: (rowData) => rowData.status === 'ERROR',
              onClick: (rowData) => {
                setTempScheduleDefinition(rowData);
                duplicateTriggerProps.onClick();
              },
            },
            {
              label: t('Delete'),
              Icon: TrashIcon,
              onClick: (rowData) => {
                setTempScheduleDefinition(rowData);
                deleteTriggerProps.onClick();
              },
            },
          ],
        }}
        styleConfig={{
          rows: (row: ScheduleDefinition) =>
            row.status === 'INACTIVE' &&
            css`
              color: ${theme.font.colors.disabled};
            `,
        }}
      />
      <DeleteConfirmationModal
        modalProps={deleteModalProps}
        availabilityRule={tempScheduleDefinition}
        onConfirm={handleDelete}
      />
      <DuplicateConfirmationModal
        resourceOptions={resourceOptions}
        modalProps={duplicateModalProps}
        onConfirm={(resourceId: string | undefined) => {
          openAddEditTray(tempScheduleDefinition, resourceId, true);
          setTempScheduleDefinition(null);
        }}
      />
    </>
  );
};

const tableStyle = css({
  borderRadius: '0 !important',
  borderTop: 'none !important',
  borderLeft: 'none !important',
  borderRight: 'none !important',
});
